import React, { useEffect, useState } from "react";
import { ProfileInterface } from "ts-interfaces";
import { useStripe, useElements,PaymentElement } from "@stripe/react-stripe-js";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../libs/firebase.js";
import { useLoginState } from "../studioxr/auth/LoginState.ts";
import { PrimaryButton } from "../studioxr/Button";
import { fetchData } from "../libs/util.ts";
import { reportBillingRefusal } from "./billingRefusal";
import { createStripeSubscriptionAPIUrlV4 } from "./config.js";
import * as Sentry from "@sentry/react";
import { peekCheckoutSource } from "./checkoutSource";
import { ProfilePrices } from "./useStripeStore.tsx";
import { PaymentMethodRequest } from "./PaymentMethodRequest.tsx";

/** What /createSubscriptionV4 returns for the card to confirm (issue #4334). */
export interface DeferredCheckout {
  kind: "payment" | "setup" | "active";
  clientSecret?: string;
  amount: number;
  currency: string;
  customerId: string;
  subscriptionId: string;
}

interface CheckoutFormProps {
  onSuccess: (customerId: string, subscriptionId: string) => void;
  selectedPlan: ProfilePrices;
  promoId: string;
  amount: number;
  currency: string;
}

// A declined card retries the same PaymentIntent; anything else (expired, replaced) starts over.
const retriesSameIntent = (error) => error?.type === "card_error" || error?.type === "validation_error";

export const CheckoutForm = ({ onSuccess, selectedPlan, promoId, amount, currency }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  // The request link only makes sense once there is a card form to look past.
  const [cardReady, setCardReady] = useState(false);
  // The subscription of a declined attempt, confirmed again on the next click.
  const [pending, setPending] = useState<DeferredCheckout | null>(null);

  // A new promo or amount means a new first invoice, so the old attempt is dropped.
  useEffect(() => { setPending(null); }, [promoId, amount, currency]);

  async function createSubscription(): Promise<DeferredCheckout> {
    const requestSubscription = {
      currency: selectedPlan.currency,
      priceId: selectedPlan.priceID,
      promoId: promoId,
      // Lets the server resolve the regional Price.
      interval: selectedPlan.planType,
      // Attribution for Stripe metadata (e.g. the SketchUp panel).
      source: peekCheckoutSource() ?? undefined,
    };
    // Required: the server files the customer under the token's email and prices from it.
    let authHeaders = {};
    try {
      const user = getAuth(firebaseApp).currentUser;
      if (user) authHeaders = { Authorization: `Bearer ${await user.getIdToken()}` };
    } catch (tokenError) {
      console.error("[CheckoutForm] Failed to get auth token:", tokenError);
    }
    const data = await fetchData(requestSubscription, createStripeSubscriptionAPIUrlV4, authHeaders, { rethrow: true });
    if (!data?.customerId || !data?.subscriptionId) {
      throw new Error(data?.error?.message || data?.error || "Error processing payment");
    }
    return data;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const profile:ProfileInterface | any = useLoginState.getState().profile
    if (!stripe || !elements) { return; }
    if (isProcessing===true) { return; }
    if (!profile?.id) { 
      setMessage( "Profile does not exist. Please login.");
      Sentry.captureException("Profile does not exist. Please login."); 
      return; 
    }
    setIsProcessing(true);
    setMessage("");
    try {
      // Validates the card form before anything is created on Stripe.
      const { error: submitError } = await elements.submit();
      if (submitError) { setMessage(submitError.message ?? ""); return; }

      let checkout = pending;
      if (!checkout) {
        try {
          checkout = await createSubscription();
        } catch (error) {
          console.error(error);
          reportBillingRefusal("createSubscription", error);
          Sentry.captureException("error processing payment", error);
          setMessage(error?.message);
          return;
        }
        setPending(checkout);
      }
      if (checkout.kind === "active") {
        onSuccess(checkout.customerId, checkout.subscriptionId);
        return;
      }

      // Charge the invoice's amount/currency (regional price, promo rounding); Stripe wants a new submit after.
      if (checkout.amount !== amount || checkout.currency !== currency) {
        elements.update({ amount: checkout.amount, currency: checkout.currency });
        const { error: resubmitError } = await elements.submit();
        if (resubmitError) { setMessage(resubmitError.message ?? ""); return; }
      }

      const confirmArgs = {
        elements,
        clientSecret: checkout.clientSecret as string,
        redirect: "if_required" as const,
        confirmParams: { return_url: `${window.location.origin}` },
      };
      const result: any = checkout.kind === "setup"
        ? await stripe.confirmSetup(confirmArgs)
        : await stripe.confirmPayment(confirmArgs);
      if (result?.error) {
        Sentry.captureException("error processing payment", result.error);
        setMessage(result.error.message);
        if (!retriesSameIntent(result.error)) setPending(null);
        return;
      }
      const status = result?.paymentIntent?.status ?? result?.setupIntent?.status;
      if (status === "succeeded") {
        onSuccess(checkout.customerId, checkout.subscriptionId);
      } else if (status === "processing") {
        // The server will not replace an attempt whose payment is processing.
        setMessage("Your bank is still processing this payment. Pro turns on once it's confirmed, so there's no need to pay again.");
      } else {
        setMessage("Your payment could not be completed. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    // Never submit natively: Enter in the request panel (in Firefox, even on a radio) would reload checkout.
    <form id="payment-form" onSubmit={(e) => e.preventDefault()}>
      <PaymentElement
        id="payment-element"
        options={{
          layout: { type: 'accordion', defaultCollapsed: false, radios: false, spacedAccordionItems: false },
          wallets: { googlePay: 'never', applePay: 'never' },
        }}
        onReady={() => setCardReady(true)}
        onLoadError={() => setCardReady(false)}
      />
      {cardReady && <PaymentMethodRequest checkout="pro" currency={selectedPlan?.currency} interval={selectedPlan?.planType} />}
      <PrimaryButton
        aria-label='GetYourFreeTrial'
        onClick={handleSubmit}
        sx={{
          marginTop: '18px',
          padding: '8px',
          fontWeight: 400,
          fontSize: '18px',
          background: '#000',
          color: '#fff',
          '&:hover': { background: '#222' },
          width: '100%',
          textTransform: 'none',
          borderRadius: '8px',
        }}
        variant='contained'
        disabled={isProcessing || !stripe || !elements}
        id="submit"
      >
        {isProcessing ? "Processing ... " : "Start Mattoboard Pro Now"}
      </PrimaryButton>
      {message && <div id="payment-message" style={{ marginTop: '10px', color: '#b42318', fontSize: '14px' }}>{message}</div>}
    </form>
  );
}
